import React from 'react';
import { useQuery, useAction } from 'wasp/client/operations';
import { 
  getNotifications, 
  markNotificationRead, 
  markAllNotificationsRead, 
  deleteNotification 
} from 'wasp/client/operations';
import { 
  Bell, 
  Check, 
  Trash2, 
  Mail, 
  Calendar, 
  Info, 
  Clock, 
  MoreVertical,
  ChevronRight,
  BellOff
} from 'lucide-react';
import { cn, formatRelativeTime } from '../../lib/utils';

export default function NotificationsPage() {
  const { data: notifications, isLoading, error, refetch } = useQuery(getNotifications, {});
  const markRead = useAction(markNotificationRead);
  const markAllRead = useAction(markAllNotificationsRead);
  const removeNotif = useAction(deleteNotification);

  const handleMarkRead = async (id: string) => {
    try {
      await markRead({ notificationId: id });
      refetch();
    } catch (err) {
      console.error('Failed to mark notification as read:', err);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await markAllRead({});
      refetch();
    } catch (err) {
      console.error('Failed to mark all notifications as read:', err);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await removeNotif({ notificationId: id });
      refetch();
    } catch (err) {
      console.error('Failed to delete notification:', err);
    }
  };

  const getIcon = (type: string) => {
    switch (type) {
      case 'CONTENT_APPROVED':
      case 'NEW_LOCATION_CONTENT':
      case 'NEW_ORGANIZATION_CONTENT':
        return <Check className="w-5 h-5 text-green-500" />;
      case 'CONTENT_REJECTED':
        return <BellOff className="w-5 h-5 text-destructive" />;
      case 'APPROVAL_REQUESTED':
        return <Clock className="w-5 h-5 text-blue-500" />;
      case 'LOCATION_INFO_UPDATED':
      case 'ORGANIZATION_INFO_UPDATED':
        return <Info className="w-5 h-5 text-amber-500" />;
      default: return <Bell className="w-5 h-5 text-primary" />;
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const unreadCount = notifications?.filter(n => !n.isRead).length || 0;

  return (
    <div className="w-full px-4 py-8">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <Bell className="w-8 h-8 text-primary" />
            Notifications
          </h1>
          <p className="text-slate-500 mt-1">
            {unreadCount > 0 
              ? `You have ${unreadCount} unread notifications` 
              : 'You are all caught up!'}
          </p>
        </div>
        
        {unreadCount > 0 && (
          <button 
            onClick={handleMarkAllRead}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary/10 text-primary hover:bg-primary/20 transition-colors font-medium text-sm"
          >
            <Check className="w-4 h-4" />
            Mark all as read
          </button>
        )}
      </div>

      {!notifications || notifications.length === 0 ? (
        <div className="bg-white border-2 border-dashed border-slate-200 rounded-2xl p-12 text-center">
          <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <BellOff className="w-8 h-8 text-slate-300" />
          </div>
          <h3 className="text-lg font-semibold text-slate-900">No notifications yet</h3>
          <p className="text-slate-500 max-w-sm mx-auto mt-2">
            When you subscribe to locations or follow organizations, you'll see updates and alerts here.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {notifications.map((notif) => (
            <div 
              key={notif.id}
              className={cn(
                "group relative bg-white border rounded-xl p-4 transition-all hover:shadow-md",
                !notif.isRead ? "border-primary/30 bg-primary/[0.02]" : "border-slate-200"
              )}
            >
              <div className="flex gap-4">
                <div className={cn(
                  "flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center",
                  !notif.isRead ? "bg-white shadow-sm" : "bg-slate-50"
                )}>
                  {getIcon(notif.type)}
                </div>

                <div className="flex-1 min-w-0 pr-12">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className={cn(
                      "font-semibold truncate",
                      !notif.isRead ? "text-slate-900" : "text-slate-700"
                    )}>
                      {notif.title}
                    </h4>
                    {!notif.isRead && (
                      <span className="w-2 h-2 rounded-full bg-primary flex-shrink-0" />
                    )}
                  </div>
                  <p className="text-sm text-slate-600 line-clamp-2 leading-relaxed">
                    {notif.message}
                  </p>
                  <div className="flex items-center gap-3 mt-3 text-xs text-slate-400">
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {formatRelativeTime(new Date(notif.createdAt))}
                    </span>
                    {notif.isRead && notif.readAt && (
                      <span className="flex items-center gap-1">
                        <Check className="w-3 h-3" />
                        Read
                      </span>
                    )}
                  </div>
                </div>

                <div className="absolute right-4 top-4 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  {!notif.isRead && (
                    <button 
                      onClick={() => handleMarkRead(notif.id)}
                      className="p-1.5 hover:bg-primary/10 rounded-lg text-primary transition-colors"
                      title="Mark as read"
                    >
                      <Check className="w-4 h-4" />
                    </button>
                  )}
                  <button 
                    onClick={() => handleDelete(notif.id)}
                    className="p-1.5 hover:bg-destructive/10 rounded-lg text-destructive transition-colors"
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}



